import { inject, Injectable } from "@angular/core";
import {
  DriverBillingAccountDto,
  PaginatedDriverBillingAccountResponse,
  CreateDriverBillingAccountRequest,
  UpdateDriverBillingAccountRequest
} from "@dtos/driver-billing-account.dto";
import { ApiService } from "@services/api.service";
import { EndPoints } from "@services/EndPoints";

@Injectable({ providedIn: 'root' })
export class DriverBillingAccountProvider {

  private readonly api = inject(ApiService);

  /**
   * OBTENER CUENTAS DE FACTURACIÓN DE UN CONDUCTOR
   * @param driverId - ID DEL CONDUCTOR
   * @param queryParams - PARÁMETROS DE CONSULTA OPCIONALES (page, page_size, search, ordering, etc.)
   * @returns RESPUESTA PAGINADA CON LAS CUENTAS DE FACTURACIÓN
   */
  async getDriverBillingAccounts(
    driverId: number,
    queryParams: Record<string, any> = {}
  ): Promise<PaginatedDriverBillingAccountResponse | null> {
    try {
      // Agregar el driver como queryParam
      const params = { ...queryParams, driver: driverId };

      console.log('[DriverBillingAccountProvider] Fetching billing accounts for driver:', driverId);
      console.log('[DriverBillingAccountProvider] URL:', EndPoints.getDriverBillingAccounts);
      console.log('[DriverBillingAccountProvider] Query params:', params);

      const response = await this.api.get<any>({
        url: EndPoints.getDriverBillingAccounts,
        queryParams: params
      });

      console.log('[DriverBillingAccountProvider] Response:', response);

      if (!response) return null;

      // Transformar los resultados a DTOs
      const results = (response.results || []).map((item: any) =>
        DriverBillingAccountDto.fromJson(item)
      );

      return {
        count: response.count || 0,
        total_pages: response.total_pages || 0,
        next: response.next || null,
        previous: response.previous || null,
        results
      };
    } catch (error) {
      console.error('Error al obtener cuentas de facturación del conductor:', error);
      return null;
    }
  }

  /**
   * OBTENER UNA CUENTA DE FACTURACIÓN ESPECÍFICA
   * @param accountId - ID DE LA CUENTA DE FACTURACIÓN
   * @returns CUENTA DE FACTURACIÓN
   */
  async getDriverBillingAccount(accountId: number): Promise<DriverBillingAccountDto | null> {
    try {
      const response = await this.api.get<any>({
        url: EndPoints.getDriverBillingAccount.replace('{accountId}', String(accountId))
      });

      if (!response) return null;

      return DriverBillingAccountDto.fromJson(response);
    } catch (error) {
      console.error('Error al obtener cuenta de facturación:', error);
      return null;
    }
  }

  /**
   * CREAR UNA CUENTA DE FACTURACIÓN
   * @param data - DATOS DE LA CUENTA DE FACTURACIÓN
   * @returns CUENTA DE FACTURACIÓN CREADA
   */
  async createDriverBillingAccount(
    data: CreateDriverBillingAccountRequest
  ): Promise<DriverBillingAccountDto | null> {
    try {
      console.log('[DriverBillingAccountProvider] Creating billing account');
      console.log('[DriverBillingAccountProvider] Data:', data);

      const response = await this.api.post<any>({
        url: EndPoints.createDriverBillingAccount,
        formParams: data
      });

      console.log('[DriverBillingAccountProvider] Create response:', response);

      if (!response) return null;

      return DriverBillingAccountDto.fromJson(response);
    } catch (error) {
      console.error('Error al crear cuenta de facturación:', error);
      return null;
    }
  }

  /**
   * ACTUALIZAR UNA CUENTA DE FACTURACIÓN
   * @param accountId - ID DE LA CUENTA DE FACTURACIÓN
   * @param data - DATOS A ACTUALIZAR (debe incluir el campo driver)
   * @returns CUENTA DE FACTURACIÓN ACTUALIZADA
   */
  async updateDriverBillingAccount(
    accountId: number,
    data: UpdateDriverBillingAccountRequest
  ): Promise<DriverBillingAccountDto | null> {
    try {
      console.log('[DriverBillingAccountProvider] Updating billing account:', accountId);
      console.log('[DriverBillingAccountProvider] Data:', data);

      const response = await this.api.put<any>({
        url: EndPoints.updateDriverBillingAccount.replace('{accountId}', String(accountId)),
        formParams: data
      });

      console.log('[DriverBillingAccountProvider] Update response:', response);

      if (!response) throw new Error('Error al actualizar cuenta de facturación');

      return DriverBillingAccountDto.fromJson(response);
    } catch (error) {
      console.error('Error al actualizar cuenta de facturación:', error);
      throw error;
    }
  }

  /**
   * ELIMINAR UNA CUENTA DE FACTURACIÓN
   * @param accountId - ID DE LA CUENTA DE FACTURACIÓN
   * @returns TRUE SI SE ELIMINÓ CORRECTAMENTE
   */
  async deleteDriverBillingAccount(accountId: number): Promise<boolean> {
    try {
      await this.api.delete({
        url: EndPoints.deleteDriverBillingAccount.replace('{accountId}', String(accountId))
      });
      return true;
    } catch (error) {
      console.error('Error al eliminar cuenta de facturación:', error);
      return false;
    }
  }
}
